import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
  message,
  Divider,
  Empty,
  Spin,
  Drawer,
} from "antd";

import {
  CarOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  CheckOutlined,
  UserOutlined,
  PhoneOutlined,
  CopyOutlined,
  WhatsAppOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

import apiClient from "@/utils/apiClient";

const { Title, Text } = Typography;

const API_BASE =
  import.meta.env.VITE_API_BASE_URL;

const TransportOperations = () => {

  const [loading, setLoading] =
    useState(false);

  const [trips, setTrips] =
    useState([]);

  const [filter, setFilter] =
    useState("ALL");

  const [selectedTrip, setSelectedTrip] =
    useState(null);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [assignModal, setAssignModal] =
    useState(false);

  const [selectedDay, setSelectedDay] =
    useState(null);

  const [summaryModal, setSummaryModal] =
    useState(false);

  const [briefModalOpen, setBriefModalOpen] =
    useState(false);

  const [briefContent, setBriefContent] =
    useState("");

  const [briefTripId, setBriefTripId] =
    useState(null);

  const [form] =
    Form.useForm();

  const [summaryForm] =
    Form.useForm();

  // =====================================================
  // FETCH TRIPS
  // =====================================================

  const fetchTrips =
    async () => {

      try {

        setLoading(true);

        const res =
          await apiClient.get(
            "/operations/transport"
          );

        setTrips(
          res?.data?.data || []
        );

      } catch (error) {

        console.error(
          "Fetch transport operations error:",
          error
        );

        message.error(
          "Failed to load transport operations"
        );

      } finally {

        setLoading(false);
      }
    };


  useEffect(() => {

    fetchTrips();

  }, []);

  // =====================================================
  // FILTERED TRIPS
  // =====================================================

  const filteredTrips =
    useMemo(() => {

      if (filter === "ALL") {
        return trips;
      }

      if (filter === "URGENT") {

        return trips.filter(
          t =>
            t.alerts?.isUrgent
        );
      }

      if (filter === "PENDING") {

        return trips.filter(
          t =>
            t.transportStatus !==
            "FULLY_ASSIGNED"
        );
      }

      if (filter === "ASSIGNED") {

        return trips.filter(
          t =>
            t.transportStatus ===
            "FULLY_ASSIGNED"
        );
      }

      return trips;

    }, [trips, filter]);

  // =====================================================
  // SUMMARY
  // =====================================================

  const summary =
    useMemo(() => {

      const totalTrips =
        trips.length;

      const urgentTrips =
        trips.filter(
          t =>
            t.alerts?.isUrgent
        ).length;

      const pendingTrips =
        trips.filter(
          t =>
            t.transportStatus !==
            "FULLY_ASSIGNED"
        ).length;

      return {
        totalTrips,
        urgentTrips,
        pendingTrips,
      };

    }, [trips]);

  // =====================================================
  // OPEN ASSIGN DAY
  // =====================================================

  const openAssignDay =
    (trip, day) => {

      setSelectedTrip(trip);
      setSelectedDay(day);

      const driverNameStr = typeof day.driver === "object" ? day.driver?.name : (day.driver || day.driverName || "");
      const driverPhoneStr = typeof day.driver === "object" ? day.driver?.phone : (day.driverPhone || "");
      const supplierStr = typeof day.supplier === "object" ? day.supplier?.name : (day.supplier || day.supplierName || "");

      form.setFieldsValue({
        vehicleType: day.vehicleType || "",
        vehicleNumber: day.vehicleNumber || "",
        pickupLocation: day.pickupLocation || "",
        dropLocation: day.dropLocation || "",
        pickupTime: day.pickupTime || "",
        dropTime: day.dropTime || "",
        reportingTime: day.reportingTime || "",
        notes: day.notes || "",
        supplier: supplierStr,
        driverName: driverNameStr,
        driverPhone: driverPhoneStr,
      });

      setAssignModal(true);
    };

  // =====================================================
  // ASSIGN SINGLE DAY
  // =====================================================

  const handleAssignDay =
    async () => {

      try {

        const values =
          await form.validateFields();

        await apiClient.put(

          `/bookings/${selectedTrip._id}/transport-day/${selectedDay.dayNumber}`,

          {
            vehicleType: values.vehicleType,
            vehicleNumber: values.vehicleNumber,
            pickupLocation: values.pickupLocation,
            dropLocation: values.dropLocation,
            pickupTime: values.pickupTime,
            dropTime: values.dropTime,
            reportingTime: values.reportingTime,
            notes: values.notes,
            supplierName: values.supplier,
            supplier: values.supplier
              ? {
                  name: values.supplier,
                  phone: "",
                }
              : null,
            driverName: values.driverName,
            driverPhone: values.driverPhone,
            driver: {
              name: values.driverName,
              phone: values.driverPhone,
            },
            status: "ASSIGNED",
          }
        );

        message.success(
          "Transport assigned successfully"
        );

        setAssignModal(false);

        fetchTrips();

      } catch (error) {

        console.error(
          "Assign transport error:",
          error
        );

        message.error(
          "Failed to assign transport"
        );
      }
    };

  // =====================================================
  // FULL TOUR ASSIGN
  // =====================================================

  const handleSummaryAssign =
    async () => {

      try {

        const values =
          await summaryForm.validateFields();

        const days = selectedTrip?.days || selectedTrip?.transportPlan?.days || [];

        if (days.length > 0) {
          for (const day of days) {
            await apiClient.put(
              `/bookings/${selectedTrip._id}/transport-day/${day.dayNumber}`,
              {
                vehicleType: values.vehicleType || day.vehicleType,
                vehicleNumber: values.vehicleNumber || day.vehicleNumber,
                supplierName: values.supplierName,
                supplier: values.supplierName ? { name: values.supplierName } : null,
                driverName: values.driverName,
                driverPhone: values.driverPhone,
                driver: { name: values.driverName, phone: values.driverPhone },
                reportingTime: values.reportingTime || day.reportingTime || "08:00 AM",
                notes: values.notes || day.notes,
                status: "ASSIGNED",
              }
            );
          }
        } else {
          await apiClient.put(
            `/bookings/${selectedTrip._id}/transport-summary`,
            {
              vehicleType: values.vehicleType,
              supplierName: values.supplierName,
              driverName: values.driverName,
              driverPhone: values.driverPhone,
              reportingTime: values.reportingTime,
              notes: values.notes,
            }
          );
        }

        message.success(
          "Full tour transport assigned to all days"
        );

        setSummaryModal(false);

        fetchTrips();

      } catch (error) {

        console.error(
          "Summary assign error:",
          error
        );

        message.error(
          "Failed to assign full tour"
        );
      }
    };

  // =====================================================
  // GENERATE BRIEF & OPEN COPY MODAL
  // =====================================================

  const generateBrief =
    async (tripId) => {

      try {

        const res = await apiClient.post(
          `/bookings/${tripId}/transport/brief`
        );

        const briefText = res.data?.data?.brief || "";

        setBriefContent(briefText);
        setBriefTripId(tripId);
        setBriefModalOpen(true);

        message.success(
          "Transport brief generated"
        );

      } catch (error) {

        console.error(error);

        message.error(
          "Failed to generate brief"
        );
      }
    };

  // =====================================================
  // DOWNLOAD PDF
  // =====================================================

const downloadPDF = async (tripId) => {

  try {

    const res = await apiClient.get(

      `/bookings/${tripId}/transport/pdf`,

      {
        responseType: "blob",
      }

    );

    const url = window.URL.createObjectURL(
      new Blob([res.data])
    );

    const a = document.createElement("a");

    a.href = url;

    a.download = `Transport-${tripId}.pdf`;

    document.body.appendChild(a);

    a.click();

    a.remove();

    window.URL.revokeObjectURL(url);

  } catch (error) {

    console.error(error);

    message.error(
      "Failed to download PDF"
    );

  }

};

  // =====================================================
  // FINALIZE
  // =====================================================

  const finalizeTransport =
    async (tripId) => {

      try {

        await apiClient.post(
          `/bookings/${tripId}/transport/finalize`
        );

        message.success(
          "Transport finalized"
        );

        fetchTrips();

      } catch (error) {

        console.error(error);

        message.error(
          "Failed to finalize transport"
        );
      }
    };

  // =====================================================
  // STATUS TAG
  // =====================================================

  const renderStatus =
    (status) => {

      if (
        status ===
        "FULLY_ASSIGNED"
      ) {

        return (
          <Tag color="green">
            FULLY ASSIGNED
          </Tag>
        );
      }

      if (
        status ===
        "PARTIAL"
      ) {

        return (
          <Tag color="orange">
            PARTIAL
          </Tag>
        );
      }

      return (
        <Tag color="red">
          NOT ASSIGNED
        </Tag>
      );
    };

  return (

    <div
      style={{
        padding: 24,
      }}
    >

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          marginBottom: 24,
        }}
      >

        <div>

          <Title
            level={2}
            style={{
              margin: 0,
            }}
          >

            <CarOutlined
              style={{
                marginRight: 10,
                color: "#1677ff",
              }}
            />

            Transport Operations

          </Title>

          <Text type="secondary">

            Day-wise transport assignment & dispatch management

          </Text>

        </div>

        <Space>

          {[
            "ALL",
            "URGENT",
            "PENDING",
            "ASSIGNED",
          ].map((f) => (

            <Button
              key={f}
              type={
                filter === f
                  ? "primary"
                  : "default"
              }
              onClick={() =>
                setFilter(f)
              }
            >
              {f}
            </Button>
          ))}

        </Space>

      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <Row
        gutter={16}
        style={{
          marginBottom: 24,
        }}
      >

        <Col span={8}>

          <Card
            bordered={false}
            style={{
              borderRadius: 20,
            }}
          >

            <Statistic
              title="Total Trips"
              value={
                summary.totalTrips
              }
            />

          </Card>

        </Col>

        <Col span={8}>

          <Card
            bordered={false}
            style={{
              borderRadius: 20,
            }}
          >

            <Statistic
              title="Pending Assignment"
              value={
                summary.pendingTrips
              }
              valueStyle={{
                color: "#faad14",
              }}
            />

          </Card>

        </Col>

        <Col span={8}>

          <Card
            bordered={false}
            style={{
              borderRadius: 20,
            }}
          >

            <Statistic
              title="Urgent Trips"
              value={
                summary.urgentTrips
              }
              valueStyle={{
                color: "#ff4d4f",
              }}
            />

          </Card>

        </Col>

      </Row>

      {/* =====================================================
          LIST
      ===================================================== */}

      {loading ? (

        <div
          style={{
            textAlign: "center",
            padding: 80,
          }}
        >

          <Spin size="large" />

        </div>

      ) : filteredTrips.length === 0 ? (

        <Empty
          description="No transport operations found"
        />

      ) : (

        <Space
          direction="vertical"
          size={16}
          style={{
            width: "100%",
          }}
        >

          {filteredTrips.map(
            (trip) => (

              <Card
                key={trip._id}
                hoverable
                style={{
                  borderRadius: 20,
                }}
              >

                <Row
                  justify="space-between"
                  align="middle"
                >

                  <Col span={6}>

                    <Space direction="vertical">

                      <Text strong>
                        {
                          trip.bookingReference
                        }
                      </Text>

                      <Text type="secondary">
                        👤 {trip.customerName || trip.customer?.name || "Guest"}
                      </Text>

                      {trip.customerPhone && (
                        <Text type="secondary">
                          <PhoneOutlined /> {trip.customerPhone}
                        </Text>
                      )}

                      <Text type="secondary">
                        📍 {typeof trip.destination === "string" ? trip.destination : (trip.destination?.city || "-")}
                      </Text>

                    </Space>

                  </Col>

                  <Col span={4}>

                    {renderStatus(
                      trip.transportStatus
                    )}

                  </Col>

                  <Col span={4}>

                    <Tag color={trip.unassignedDays > 0 ? "orange" : "green"}>

                      {
                        trip.unassignedDays
                      } {trip.unassignedDays === 1 ? "Day Pending" : "Days Pending"}

                    </Tag>

                  </Col>

                  <Col span={10}>

                    <Space wrap>

                      <Button
                        type="primary"
                        onClick={() => {
                          window.location.href = `/trips/${trip._id}?tab=operations`;
                        }}
                      >
                        Trip Operations
                      </Button>

                      <Button
                        onClick={() => {

                          setSelectedTrip(
                            trip
                          );

                          setDrawerOpen(
                            true
                          );
                        }}
                      >

                        View Days

                      </Button>

                      <Button
                        icon={
                          <FileTextOutlined />
                        }
                        onClick={() =>
                          generateBrief(
                            trip._id
                          )
                        }
                      >

                        Brief

                      </Button>

                      <Button
                        icon={
                          <FilePdfOutlined />
                        }
                        onClick={() =>
                          downloadPDF(
                            trip._id
                          )
                        }
                      >

                        PDF

                      </Button>


<Button
  onClick={async () => {
    // Authenticated download (window.open sent no token and doubled /api).
    try {
      const res = await apiClient.get(`/bookings/${trip._id}/voucher`, {
        responseType: "blob",
        bypassCache: true,
      });
      const blob = res?.data instanceof Blob ? res.data : new Blob([res?.data ?? res], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `voucher-${trip.bookingReference || trip._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      message.error("Failed to download voucher");
    }
  }}
>
  Voucher
</Button>

                      <Button
                        type="primary"
                        onClick={() => {

                          setSelectedTrip(
                            trip
                          );

                          setSummaryModal(
                            true
                          );
                        }}
                      >

                        Full Tour Assign

                      </Button>

                      <Button
                        danger
                        icon={
                          <CheckOutlined />
                        }
                        onClick={() =>
                          finalizeTransport(
                            trip._id
                          )
                        }
                      >

                        Finalize

                      </Button>

                    </Space>

                  </Col>

                </Row>

              </Card>
            )
          )}

        </Space>
      )}

      {/* =====================================================
          DAY DRAWER
      ===================================================== */}

      <Drawer
        title="Trip Transport Plan"
        width={900}
        open={drawerOpen}
        onClose={() =>
          setDrawerOpen(false)
        }
      >

        <Space
          direction="vertical"
          size={16}
          style={{
            width: "100%",
          }}
        >

          {selectedTrip?.days?.map(
            (day) => (

              <Card
                key={day.dayNumber}
                style={{
                  borderRadius: 16,
                }}
              >

                <Row
                  justify="space-between"
                >

                  <Col span={16}>

                    <Space
                      direction="vertical"
                    >

                      <Title
                        level={5}
                      >

                        Day {
                          day.dayNumber
                        }

                      </Title>

                      <Text>

                        <EnvironmentOutlined />

                        {" "}

                        {
                          day.pickupLocation
                        }

                        {" → "}

                        {
                          day.dropLocation
                        }

                      </Text>

                      <Text>
                        Vehicle: {day.vehicleType || "-"} {day.vehicleNumber ? `[${day.vehicleNumber}]` : ""}
                      </Text>

                      <Text>
                        Supplier: {day.supplier?.name || day.supplier || day.supplierName || "-"}
                      </Text>

                      <Text>
                        Driver: {day.driver?.name || day.driver || day.driverName || "-"} {day.driver?.phone || day.driverPhone ? `(Ph: ${day.driver?.phone || day.driverPhone})` : ""}
                      </Text>

                      {day.reportingTime && (
                        <Text>
                          <ClockCircleOutlined /> Reporting Time: {day.reportingTime}
                        </Text>
                      )}

                    </Space>

                  </Col>

                  <Col>

                    <Space
                      direction="vertical"
                    >

                      <Tag
                        color={
                          day.status ===
                          "ASSIGNED"
                            ? "green"
                            : "orange"
                        }
                      >

                        {day.status}

                      </Tag>

                      <Button
                        type="primary"
                        onClick={() =>
                          openAssignDay(
                            selectedTrip,
                            day
                          )
                        }
                      >

                        Assign Day

                      </Button>

                    </Space>

                  </Col>

                </Row>

              </Card>
            )
          )}

        </Space>

      </Drawer>

      {/* =====================================================
          ASSIGN DAY MODAL
      ===================================================== */}

      <Modal
        title={`Assign Day ${selectedDay?.dayNumber}`}
        open={assignModal}
        onCancel={() =>
          setAssignModal(false)
        }
        onOk={handleAssignDay}
        width={720}
      >

        <Form
          layout="vertical"
          form={form}
        >

          <Row gutter={16}>

            <Col span={8}>

              <Form.Item
                label="Vehicle Type"
                name="vehicleType"
              >

                <Input placeholder="e.g. Innova / Sedan" />

              </Form.Item>

            </Col>

            <Col span={8}>

              <Form.Item
                label="Vehicle Reg No."
                name="vehicleNumber"
              >

                <Input placeholder="e.g. DL-01-AB-1234" />

              </Form.Item>

            </Col>

            <Col span={8}>

              <Form.Item
                label="Supplier Name"
                name="supplier"
              >

                <Input placeholder="Type supplier name" />

              </Form.Item>

            </Col>

          </Row>

          <Row gutter={16}>

            <Col span={12}>

              <Form.Item
                label="Driver Name"
                name="driverName"
              >

                <Input
                  prefix={
                    <UserOutlined />
                  }
                  placeholder="Driver full name"
                />

              </Form.Item>

            </Col>

            <Col span={12}>

              <Form.Item
                label="Driver Phone"
                name="driverPhone"
              >

                <Input
                  prefix={
                    <PhoneOutlined />
                  }
                  placeholder="Driver mobile number"
                />

              </Form.Item>

            </Col>

          </Row>

          <Row gutter={16}>

            <Col span={12}>

              <Form.Item
                label="Pickup Location"
                name="pickupLocation"
              >

                <Input placeholder="Airport / Hotel / Station" />

              </Form.Item>

            </Col>

            <Col span={12}>

              <Form.Item
                label="Drop Location"
                name="dropLocation"
              >

                <Input placeholder="Destination / Sightseeing" />

              </Form.Item>

            </Col>

          </Row>

          <Row gutter={16}>

            <Col span={12}>

              <Form.Item
                label="Pickup Time"
                name="pickupTime"
              >

                <Input placeholder="e.g. 09:00 AM" />

              </Form.Item>

            </Col>

            <Col span={12}>

              <Form.Item
                label="Reporting Time"
                name="reportingTime"
              >

                <Input placeholder="e.g. 08:30 AM" />

              </Form.Item>

            </Col>

          </Row>

          <Form.Item
            label="Notes / Special Instructions"
            name="notes"
          >

            <Input.TextArea
              rows={3}
              placeholder="e.g. Parking inclusion, toll fees, driver allowance details..."
            />

          </Form.Item>

        </Form>

      </Modal>

      {/* =====================================================
          FULL TOUR MODAL
      ===================================================== */}

      <Modal
        title="Full Tour Transport Assignment"
        open={summaryModal}
        onCancel={() =>
          setSummaryModal(false)
        }
        onOk={handleSummaryAssign}
        width={680}
      >

        <Form
          layout="vertical"
          form={summaryForm}
        >

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Vehicle Type"
                name="vehicleType"
              >
                <Input placeholder="e.g. Innova / Tempo Traveller" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Vehicle Reg No."
                name="vehicleNumber"
              >
                <Input placeholder="e.g. DL-01-AB-1234" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Supplier Name"
                name="supplierName"
              >
                <Input placeholder="Transport vendor name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Reporting Time"
                name="reportingTime"
              >
                <Input placeholder="e.g. 08:00 AM" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Driver Name"
                name="driverName"
              >
                <Input prefix={<UserOutlined />} placeholder="Driver name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Driver Phone"
                name="driverPhone"
              >
                <Input prefix={<PhoneOutlined />} placeholder="Driver contact phone" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Notes for Full Tour"
            name="notes"
          >

            <Input.TextArea
              rows={3}
              placeholder="Special instructions applicable to all tour days..."
            />

          </Form.Item>

        </Form>

      </Modal>

      {/* =====================================================
          TRANSPORT BRIEF MODAL (COPY & SHARE)
      ===================================================== */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: "#1890ff" }} />
            <span>Transport Operations Brief</span>
          </Space>
        }
        open={briefModalOpen}
        onCancel={() => setBriefModalOpen(false)}
        width={720}
        footer={[
          <Button key="close" onClick={() => setBriefModalOpen(false)}>
            Close
          </Button>,
          <Button
            key="whatsapp"
            icon={<WhatsAppOutlined />}
            style={{ backgroundColor: "#25D366", color: "#fff", borderColor: "#25D366" }}
            onClick={() => {
              window.open(`https://wa.me/?text=${encodeURIComponent(briefContent)}`, "_blank");
            }}
          >
            WhatsApp Driver
          </Button>,
          <Button
            key="pdf"
            icon={<FilePdfOutlined />}
            onClick={() => downloadPDF(briefTripId)}
          >
            Download PDF
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(briefContent);
              message.success("Transport brief copied to clipboard! Ready to share with driver or operator.");
            }}
          >
            Copy Brief
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary">
          Generated transport routing brief for driver and operator dispatch. You can copy the text below to send via WhatsApp/SMS or download the official PDF.
        </Typography.Paragraph>

        <Input.TextArea
          value={briefContent}
          onChange={(e) => setBriefContent(e.target.value)}
          rows={14}
          style={{ fontFamily: "monospace", fontSize: 13, borderRadius: 8 }}
        />
      </Modal>

    </div>
  );
};

export default TransportOperations;
